/**
 * FRIDGE-611 — the preferred-source prompt experiment, pure rules.
 *
 * Owner answer 09/24/2026: "Agree", and make it happen automatically with the
 * real logos. The bounded shape (09/17 comment on the card): a small
 * dismissible slide-in — never a full-screen interstitial — desktop-weighted,
 * shown only after the reader scrolls, only on the week's ten busiest
 * articles, at most once per visitor per cooldown, and measured against a
 * control group that never sees it. Google's own control still makes the
 * choice; nothing opens automatically.
 *
 * Kept free of React and the DOM so the rules are unit-tested directly
 * (tests/preferred-source-prompt-fridge-611.test.ts).
 */

export const PROMPT_STORAGE_KEY = 'fridge_pref_src_prompt_v1'

/** Once shown or dismissed, the prompt rests for 30 days. */
export const PROMPT_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000

/** Desktop-weighted: phones never get it (Google's intrusive-interstitial guidance). */
export const PROMPT_MIN_WIDTH = 768

/** Seconds after the first real scroll before the slide-in appears. */
export const PROMPT_DELAY_MS = 8000

export type PromptBucket = 'prompt' | 'control'

export interface PromptState {
  bucket: PromptBucket
  shownAt?: number
  dismissedAt?: number
  clickedAt?: number
}

/** Even split. `rand` is Math.random() at first eligibility. */
export function assignBucket(rand: number): PromptBucket {
  return rand < 0.5 ? 'prompt' : 'control'
}

export function parsePromptState(raw: string | null): PromptState | null {
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as Partial<PromptState>
    if (v.bucket !== 'prompt' && v.bucket !== 'control') return null
    const out: PromptState = { bucket: v.bucket }
    for (const k of ['shownAt', 'dismissedAt', 'clickedAt'] as const) {
      if (typeof v[k] === 'number' && Number.isFinite(v[k])) out[k] = v[k]
    }
    return out
  } catch {
    return null
  }
}

export function serializePromptState(state: PromptState): string {
  return JSON.stringify(state)
}

/**
 * Control never sees it. A reader who pressed the button is done for good;
 * a shown or dismissed prompt rests for the cooldown.
 */
export function shouldShowPrompt(state: PromptState, now: number): boolean {
  if (state.bucket !== 'prompt') return false
  if (state.clickedAt) return false
  const last = Math.max(state.shownAt ?? 0, state.dismissedAt ?? 0)
  return !last || now - last > PROMPT_COOLDOWN_MS
}

function articlePath(url: string): string | null {
  try {
    const p = new URL(url, 'https://fridge.com').pathname.replace(/\/+$/, '')
    return /^\/blogs\/news\/[^/]+$/.test(p) ? p : null
  } catch {
    return null
  }
}

/** Is this page one of the week's busiest articles (the trending feed's URLs)? */
export function isTopArticle(pathname: string, topUrls: readonly string[]): boolean {
  const here = articlePath(pathname)
  if (!here) return false
  return topUrls.some((u) => articlePath(u) === here)
}
